import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, TrendingDown, Timer, FileText, Droplets, ChevronDown } from "lucide-react";
import { Slider } from "../ui/slider";
import { Input } from "../ui/input";
import { Reveal, formatINR } from "./Reveal";
import { useSiteContent } from "../../context/SiteContent";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const TARIFF_MULT = [1.0, 1.01, 1.02, 1.03, 1.04, 1.05, 1.06, 1.07, 1.08, 1.11];

function money(v) {
  const a = Math.abs(v);
  if (a >= 1e7) return `₹ ${(v / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹ ${(v / 1e5).toFixed(2)} L`;
  return `₹ ${formatINR(v)}`;
}

function computeRoi({ capacity, dailyYield, tariff, soiling, monthlyCleaning, robots, pricePerRobot, maintPerUnit }) {
  const yieldY = [];
  yieldY[0] = dailyYield;
  yieldY[1] = yieldY[0] * 0.98;
  for (let y = 2; y <= 8; y++) yieldY[y] = yieldY[y - 1] * 0.9945;
  yieldY[9] = yieldY[8] * 0.98;

  const soilFrac = soiling / 100;
  let manual = monthlyCleaning;
  const capex = robots * pricePerRobot;

  const rows = [];
  let totalLoss = 0;
  let totalRoboticSpend = capex;
  const annualLosses = [];
  const roboticByYear = [];
  let maint = 0;

  for (let i = 0; i < 10; i++) {
    const dailyGen = capacity * yieldY[i];
    const dailyUnitsLost = dailyGen * soilFrac;
    const monthlySoiling = dailyUnitsLost * (tariff * TARIFF_MULT[i]) * 30.4;
    if (i > 0) manual = manual * 1.1;
    const annualSoiling = monthlySoiling * 12;
    const annualManual = manual * 12;
    const annualLoss = annualSoiling + annualManual;
    annualLosses.push(annualLoss);
    totalLoss += annualLoss;

    let robotic;
    if (i === 0) robotic = capex;
    else if (i === 1) { maint = robots * maintPerUnit; robotic = maint; }
    else if (i === 2) { maint = maint * 1.05; robotic = maint; }
    else { maint = maint * 1.025; robotic = maint; }
    roboticByYear.push(robotic);
    if (i > 0) totalRoboticSpend += robotic;

    rows.push({ year: i + 1, dailyGen, annualSoiling, annualManual, annualLoss, robotic, cumNet: 0 });
  }
  let cumNet = 0;
  rows.forEach((r) => { cumNet += r.annualLoss - r.robotic; r.cumNet = cumNet; });

  let cumSavings = 0, cumCost = capex, payback = null;
  for (let i = 0; i < 10; i++) {
    const ms = annualLosses[i] / 12;
    const mm = (i === 0 ? 0 : roboticByYear[i]) / 12;
    for (let m = 1; m <= 12; m++) {
      cumSavings += ms; cumCost += mm;
      if (payback === null && cumSavings >= cumCost) payback = i * 12 + m;
    }
  }
  return { rows, totalLoss, totalRoboticSpend, netRoi: totalLoss - totalRoboticSpend, payback, water: capacity * 3.5 * 52 };
}

function DualInput({ label, value, unit, min, max, step, onChange, testid, decimals = 0, prefix = "" }) {
  const set = (raw) => { const n = parseFloat(raw); onChange(isNaN(n) ? min : clamp(n, min, max)); };
  const display = decimals ? Number(value).toFixed(decimals) : value;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="text-sm font-medium text-helios-slate">{label}</span>
        <div className="flex items-center rounded-lg border border-helios-line bg-helios-mist overflow-hidden">
          {prefix && <span className="pl-2.5 font-mono text-xs text-slate-400">{prefix}</span>}
          <Input type="number" value={display} min={min} max={max} step={step} onChange={(e) => set(e.target.value)}
            data-testid={`${testid}-input`} className="w-24 h-9 border-0 bg-transparent font-mono text-sm font-semibold text-helios-cyan text-right focus-visible:ring-0 px-1.5" />
          <span className="pr-2.5 font-mono text-[10px] text-slate-400 whitespace-nowrap">{unit}</span>
        </div>
      </div>
      <div className="roi-slider">
        <Slider value={[Number(value)]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} data-testid={`${testid}-slider`} />
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone, testid }) {
  const bg = tone === "gain" ? "bg-helios-slate border-helios-slate" : "bg-white border-helios-line";
  const vt = { loss: "text-red-500", gain: "text-emerald-400", payback: "text-helios-slate", water: "text-helios-cyan" }[tone];
  const lt = tone === "gain" ? "text-slate-300" : "text-slate-500";
  const it = tone === "gain" ? "text-helios-amber" : "text-helios-cyan";
  return (
    <div className={`rounded-2xl border p-6 ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3"><Icon size={16} className={it} /><span className={`font-mono text-[10px] uppercase tracking-widest ${lt}`}>{label}</span></div>
      <motion.p key={value} initial={{ opacity: 0.4, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        className={`font-mono font-bold tracking-tight text-2xl md:text-[28px] leading-tight ${vt}`}>{value}</motion.p>
    </div>
  );
}

export const RoiCalculator = ({ onReport }) => {
  const { content } = useSiteContent();
  const d = content?.roiDefaults || {};
  const [capacity, setCapacity] = useState(d.defaultCapacityKwp || 1000);
  const [dailyYield, setDailyYield] = useState(d.defaultYieldKwh || 4.2);
  const [tariff, setTariff] = useState(d.defaultTariffRs || 6.0);
  const [soiling, setSoiling] = useState(d.defaultSoilingPct || 10);
  const [monthlyCleaning, setMonthlyCleaning] = useState(d.defaultManualCostMonthly || 10000);
  const [robots, setRobots] = useState(20);
  const [expanded, setExpanded] = useState(false);

  const pricePerRobot = d.pricePerRobot || 150000;
  const maintPerUnit = d.robotMaintenanceAnnualPerUnit || 5000;

  const r = useMemo(() => computeRoi({ capacity, dailyYield, tariff, soiling, monthlyCleaning, robots, pricePerRobot, maintPerUnit }),
    [capacity, dailyYield, tariff, soiling, monthlyCleaning, robots, pricePerRobot, maintPerUnit]);

  const paybackLabel = r.payback ? `${r.payback} mo` : "120+ mo";
  const snapshot = {
    "Plant Capacity": `${formatINR(capacity)} kWp`,
    "Total Robots": `${robots} units`,
    "Soiling Loss": `${soiling}%`,
    "10-Year Loss w/o Robots": money(r.totalLoss),
    "Net 10-Year Savings": money(r.netRoi),
    "Payback Period": paybackLabel,
  };

  return (
    <section id="roi" className="py-24 md:py-32 bg-helios-mist">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Reveal className="max-w-2xl mb-14">
          <p className="overline text-helios-cyan mb-3">Financial Feasibility Engine</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-helios-slate leading-[1.05]">
            Model your 10-year ROI in real time.
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">
            Enterprise-grade financial simulation factoring in module degradation, tariff escalation,
            labor inflation, and robotic lifecycle CapEx &amp; OpEx across a full decade.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-5">
            <div className="rounded-2xl border border-helios-line bg-white p-7 space-y-7" data-testid="roi-inputs">
              <DualInput label="Plant Capacity" value={capacity} unit="kWp" min={100} max={50000} step={50} onChange={setCapacity} testid="roi-capacity" />
              <DualInput label="Specific Daily Yield" value={dailyYield} unit="kWh/kWp/d" min={3} max={6} step={0.05} onChange={setDailyYield} testid="roi-yield" decimals={2} />
              <DualInput label="Electricity Tariff" value={tariff} unit="/kWh" min={2.5} max={15} step={0.25} onChange={setTariff} testid="roi-tariff" decimals={2} prefix="₹" />
              <DualInput label="Estimated Soiling Loss" value={soiling} unit="%" min={3} max={30} step={1} onChange={setSoiling} testid="roi-soiling" />
              <DualInput label="Monthly Manual Cleaning Cost" value={monthlyCleaning} unit="/mo" min={1000} max={500000} step={1000} onChange={setMonthlyCleaning} testid="roi-cleaning" prefix="₹" />
              <DualInput label="Total Robots Deployed" value={robots} unit="units" min={1} max={200} step={1} onChange={setRobots} testid="roi-robots" />
            </div>
          </Reveal>

          <div className="lg:col-span-7 space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <Reveal delay={0.05}><KpiCard icon={TrendingDown} label="10-Yr Loss Without Robots" value={money(r.totalLoss)} tone="loss" testid="roi-out-loss" /></Reveal>
              <Reveal delay={0.1}><KpiCard icon={Zap} label="Net 10-Year Savings with Helios" value={money(r.netRoi)} tone="gain" testid="roi-out-savings" /></Reveal>
              <Reveal delay={0.15}><KpiCard icon={Timer} label="Estimated Payback Period" value={paybackLabel} tone="payback" testid="roi-out-payback" /></Reveal>
              <Reveal delay={0.2}><KpiCard icon={Droplets} label="Annual Water Conserved" value={`${formatINR(r.water)} L`} tone="water" testid="roi-out-water" /></Reveal>
            </div>

            <Reveal delay={0.15}>
              <div className="rounded-2xl border border-helios-line bg-white overflow-hidden">
                <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-helios-mist transition-colors" data-testid="roi-breakdown-toggle">
                  <span className="font-display font-bold text-helios-slate">10-Year Detailed Breakdown</span>
                  <ChevronDown size={18} className={`text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {expanded && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
                      <div className="overflow-x-auto border-t border-helios-line" data-testid="roi-breakdown-table">
                        <table className="w-full text-right font-mono text-[11.5px]">
                          <thead>
                            <tr className="bg-helios-mist text-slate-500">
                              <th className="px-3 py-2.5 text-left font-medium">Yr</th>
                              <th className="px-3 py-2.5 font-medium">Daily Units</th>
                              <th className="px-3 py-2.5 font-medium">Soiling Loss</th>
                              <th className="px-3 py-2.5 font-medium">Manual Clean</th>
                              <th className="px-3 py-2.5 font-medium">Loss w/o Robots</th>
                              <th className="px-3 py-2.5 font-medium">Robotic Spend</th>
                              <th className="px-3 py-2.5 font-medium">Cum. Net Savings</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.rows.map((row) => (
                              <tr key={row.year} className="border-t border-helios-line/70">
                                <td className="px-3 py-2 text-left text-helios-slate font-semibold">{row.year}</td>
                                <td className="px-3 py-2 text-slate-500">{formatINR(row.dailyGen)}</td>
                                <td className="px-3 py-2 text-slate-600">{money(row.annualSoiling)}</td>
                                <td className="px-3 py-2 text-slate-600">{money(row.annualManual)}</td>
                                <td className="px-3 py-2 text-red-500">{money(row.annualLoss)}</td>
                                <td className="px-3 py-2 text-slate-600">{money(row.robotic)}</td>
                                <td className={`px-3 py-2 font-semibold ${row.cumNet >= 0 ? "text-emerald-600" : "text-slate-500"}`}>{money(row.cumNet)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <button onClick={() => onReport({ leadType: "feasibility", plantCapacity: capacity, robotLine: `${robots} units`, roiSnapshot: snapshot })}
                className="btn-sheen w-full inline-flex items-center justify-center gap-2 rounded-full bg-helios-cyan px-6 py-4 text-sm font-semibold text-white hover:bg-helios-cyan/90 transition-colors"
                data-testid="roi-report-cta">
                <FileText size={16} /> Download Custom 10-Year Feasibility Report
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
